import { useQueryClient } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { trackRudderstackEvent } from '@/hooks/useRudderstackAnalytics'
import useSessionStore from '@/stores/useSessionStore'
import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

const DECAGON_SCRIPT_ID = 'decagon-loader'
const DECAGON_LOADER_SRC = 'https://decagon.ai/loaders/release/base/v1.js'
const DECAGON_SLUG = process.env.NEXT_PUBLIC_DECAGON_SLUG
// Decagon has no client-side getter for the widget's anonymous user id (it lives
// in their cross-origin iframe), so we mint and persist our OWN
// decagon_anonymous_<uuid> id. Knowing it lets us rebind the anonymous
// conversation to the authenticated user on login (server-side /conversation/link).
// The id survives a login page-reload, so the post-login load links the thread.
const DECAGON_ANON_ID_KEY = 'decagon_anon_user_id'

// The working branch (Agent Versioning workspace) the conversation should run.
// Applied as `_decagon_branch_name` metadata so the embedded widget uses THAT
// branch's tools + AOP — no push to staging/production required.
//
// localStorage wins over the env var so a tester on a DEPLOYED server (where the
// env is fixed) can pin their own browser: localStorage.setItem('decagon_branch',
// 'garfield') and refresh. Not a security boundary — the pin is client metadata
// the platform accepts from any visitor's console regardless of this code.
const DECAGON_BRANCH_STORAGE_KEY = 'decagon_branch'
const getBranch = (): string | undefined => {
  try {
    const stored = window.localStorage.getItem(DECAGON_BRANCH_STORAGE_KEY)
    if (stored && stored.trim()) return stored.trim()
  } catch {
    // storage unavailable (private mode / blocked) — fall through to env
  }
  return process.env.NEXT_PUBLIC_DECAGON_BRANCH || undefined
}

let decagonLoadScheduled = false

// A session token that arrived FROM the chat — the set_auth relay, or the
// in-chat sign-in popover. useDecagon's auth-change effect normally
// re-identifies the widget on a token change: fetch /decagon/token, setUserId
// (which refetches conversation history), then setMetadata — which REPLACES
// the whole metadata object with buildDecagonMetadata(auth). For a
// chat-originated sign-in that is exactly wrong: the conversation already knows
// who this is — the tool (or sendIdentity) just wrote customer_* / payment_nonce
// / billing_* — and the replace would land a few hundred ms later, mid-turn,
// erasing them; @identify_customer then reads customer_user_id = null and books
// a GUEST, the defect a35958a5 closed. So both chat paths mark the token here
// and the effect skips the re-identify for it. Linking the anon conversation to
// the account still happens on the next page-load identify, as it always did
// for reload logins.
interface ChatSignInMark {
  token: string
  at: number
}

// The mark lives in localStorage, not in a module variable, because the sign-in propagates to
// EVERY OPEN TAB: SessionStoreSync calls setAuth on the `storage` event, so each tab's
// useDecagon sees an auth change and would run identifyDecagonUser — whose setMetadata REPLACES
// the conversation metadata the tool just wrote. Every tab shares one anon conversation id, so a
// single stale background tab was enough to reopen the guest-booking defect this mark exists to
// prevent. A module variable is invisible to the other tabs; localStorage is not.
const CHAT_SIGNIN_KEY = 'decagon_chat_signin'
// Correctness rests on TOKEN IDENTITY, not on the clock. The TTL is only garbage collection, so
// it is generous: a 60s window made the guard wall-clock-local, which is the same class of bug as
// the tab-local one above. Chrome FREEZES background tabs — a frozen tab runs no tasks and gets
// its queued `storage` event on unfreeze, potentially long after the sign-in. With a short TTL
// that tab would re-identify and REPLACE the conversation metadata mid-turn: the exact wipe this
// mark prevents, from a tab nobody is looking at.
const CHAT_SIGNIN_TTL_MS = 24 * 60 * 60 * 1000

// Fallback for private mode / blocked storage, so the marking tab still works.
let localMark: ChatSignInMark | null = null
// Per-TAB, so each tab skips the re-identify at most once per token. Deliberately not a delete
// of the shared mark: the other tabs still have to read it.
let consumedChatToken: string | null = null

function readChatSignInMarks(): ChatSignInMark[] {
  let stored: ChatSignInMark | null = null
  try {
    const raw = window.localStorage.getItem(CHAT_SIGNIN_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ChatSignInMark>
      if (typeof parsed?.token === 'string' && typeof parsed?.at === 'number') {
        stored = { token: parsed.token, at: parsed.at }
      }
    }
  } catch {
    stored = null
  }
  // BOTH candidates, never one shadowing the other. Preferring the stored mark whenever it merely
  // parsed made the in-memory fallback unreachable in the case it exists for — setItem throwing
  // (quota, Safari private mode, a blocking extension) while an earlier mark still sits on disk.
  // Preferring the NEWER one instead just moved the shadowing: a fresh local mark for token A
  // would hide a perfectly valid stored mark for token B, written by another tab. The question is
  // not "which mark is best" but "does any mark match the token we are being asked about".
  return [stored, localMark].filter((m): m is ChatSignInMark => m !== null)
}

/** Record that `token` came from the chat, so the next auth-change effect does not re-identify. */
export function markChatSignIn(token: string): void {
  const mark: ChatSignInMark = { token, at: Date.now() }
  localMark = mark
  try {
    window.localStorage.setItem(CHAT_SIGNIN_KEY, JSON.stringify(mark))
  } catch {
    // Private mode: the in-memory mark still covers this tab.
  }
}

/** True — once per tab, within the TTL — when `token` is the one marked by markChatSignIn. */
function consumeChatSignIn(token: string | null): boolean {
  if (!token) return false
  for (const mark of readChatSignInMarks()) {
    if (mark.token !== token) continue
    if (Date.now() - mark.at > CHAT_SIGNIN_TTL_MS) continue
    // Keyed on token AND mark timestamp, so a genuinely NEW mark for a token this tab has already
    // skipped once (sign out, sign back in, same token) is still honoured.
    const key = `${token}@${mark.at}`
    if (consumedChatToken === key) return false
    consumedChatToken = key
    return true
  }
  return false
}

interface SetAuthPayload {
  token: string
  userId?: string
  userEmail?: string
  userRoles?: string[]
  mobileVerified?: boolean
  termsAccepted?: boolean
}

// The set_auth payload is authored by the AOP tool on Decagon's side — whichever
// branch happens to be pinned — and setAuth persists whatever it is handed to
// localStorage. Only well-typed fields get through: a non-array userRoles would
// make every `userRoles.includes(...)` in ClientPortal throw on render, outside
// any error boundary, and survive reload because it is persisted. `null` is
// treated as absent. No token means nothing to sign in with — never clear a
// live session on a malformed event, which setAuth(null, …) would do.
function parseSetAuthPayload(raw: unknown): SetAuthPayload | null {
  const data = (raw ?? {}) as Record<string, unknown>
  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)
  const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined)
  const wellTyped = (v: unknown, ok: (x: unknown) => boolean): boolean =>
    v === undefined || v === null || ok(v)

  // .trim(): a whitespace-only token is truthy, and would persist a junk session that survives
  // a reload.
  if (typeof data.token !== 'string' || !data.token.trim()) {
    // eslint-disable-next-line no-console
    console.warn('[decagon] ignoring set_auth without a token')
    return null
  }
  const typed =
    wellTyped(data.userId, (v) => typeof v === 'string') &&
    wellTyped(data.userEmail, (v) => typeof v === 'string') &&
    wellTyped(data.userRoles, (v) => Array.isArray(v) && v.every((r) => typeof r === 'string')) &&
    wellTyped(data.mobileVerified, (v) => typeof v === 'boolean') &&
    wellTyped(data.termsAccepted, (v) => typeof v === 'boolean')
  if (!typed) {
    // eslint-disable-next-line no-console
    console.warn('[decagon] ignoring malformed set_auth payload')
    return null
  }
  return {
    // TRIMMED: validating `.trim()` but storing the raw value persisted the junk session the
    // check exists to stop, and marked a token that would not match anywhere else.
    token: data.token.trim(),
    userId: str(data.userId),
    userEmail: str(data.userEmail),
    userRoles: Array.isArray(data.userRoles) ? (data.userRoles as string[]) : undefined,
    mobileVerified: bool(data.mobileVerified),
    termsAccepted: bool(data.termsAccepted),
  }
}

declare global {
  interface Window {
    duet?: ((...args: unknown[]) => void) & {
      open?: () => void
      setEventListener?: (
        cb: (event: {
          eventName?: string
          // CLIENT_EVENT payloads: the tool-defined event name and its data.
          name?: string
          data?: Record<string, unknown>
        }) => void,
      ) => void
      setUserId?: (userId: string) => void
      setUserIdAuth?: (signature: string, epoch: number) => void
      setMetadata?: (metadata: Record<string, unknown>) => void
    }
  }
}

// Response from mono's /api/v5/decagon/token: the signing token (user_id/epoch/
// signature) plus user metadata from Decagon::UserMetadata. Metadata fields are
// forwarded verbatim to setMetadata (see buildDecagonMetadata), so new keys added
// on mono flow through without changes here.
interface DecagonAuth {
  user_id: string
  epoch: number
  signature: string
  email?: string
  name?: string
  first_name?: string
  last_name?: string
  account_type?: string
  is_vip?: boolean
  phone_number?: string
  phone_verified?: boolean
  mobile_verification_number?: string
  terms_accepted?: boolean
  country_code?: string
  currency?: string
  sms_opted_in?: boolean
}

// Auth-only keys (used for setUserId/setUserIdAuth); never forwarded to metadata.
const DECAGON_AUTH_KEYS = ['user_id', 'epoch', 'signature']

/** True when a Decagon slug is configured (env var, set via Hydra/SST). */
export const isDecagonEnabled = (): boolean => Boolean(DECAGON_SLUG)

function injectDecagonScript(slug: string): void {
  if (document.getElementById(DECAGON_SCRIPT_ID)) return

  const script = document.createElement('script')
  script.id = DECAGON_SCRIPT_ID
  script.async = true
  script.src = DECAGON_LOADER_SRC
  script.setAttribute('data-slug', slug)
  document.body.appendChild(script)
}

// Signature-auth token for the logged-in user, signed server-side by mono (the
// only holder of the Signature Auth Key). Returns null for anonymous visitors
// or on any failure — the widget then runs anonymously.
async function fetchDecagonAuth(): Promise<DecagonAuth | null> {
  const authToken = useSessionStore.getState().authToken
  if (!authToken) return null

  try {
    const res = await fetch(getV5ApiUrl('/decagon/token'), {
      headers: {
        Accept: 'application/json',
        'x-brand-id': brand.key,
        'x-user-token': authToken,
        Authorization: `Bearer ${authToken}`,
      },
    })
    if (!res.ok) throw new Error(`decagon token request failed: ${res.status}`)
    return (await res.json()) as DecagonAuth
  } catch (err) {
    // Fall back to anonymous, but surface why instead of silently swallowing.
    // eslint-disable-next-line no-console
    console.warn('[decagon] signature-auth token fetch failed; running anonymously', err)
    return null
  }
}

// Identify the user to Decagon: setUserId with the exact id mono signed, paired
// with setUserIdAuth (never one without the other). Metadata is sent for EVERY
// visitor (anon + signed-in).
// Cached across client-side navigation so refreshDecagonMetadata can re-send the
// full object (with identity) without re-fetching the signed token every route.
let cachedAuth: DecagonAuth | null = null

function readAnonId(): string | null {
  return typeof window === 'undefined' ? null : window.localStorage.getItem(DECAGON_ANON_ID_KEY)
}

// Crypto-only random token (no Math.random) — randomUUID in secure contexts,
// getRandomValues otherwise. Both are available in browsers and jsdom.
function randomToken(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function getOrCreateAnonId(): string {
  const existing = readAnonId()
  if (existing) return existing
  const id = `decagon_anonymous_${randomToken()}`
  window.localStorage.setItem(DECAGON_ANON_ID_KEY, id)
  return id
}

// Rebind our anonymous conversation to the authenticated user via mono (which
// holds the Decagon API key → POST /conversation/link). Returns true on success.
async function linkDecagonConversation(oldUserId: string): Promise<boolean> {
  const authToken = useSessionStore.getState().authToken
  if (!authToken) return false
  try {
    const res = await fetch(getV5ApiUrl('/decagon/link'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-brand-id': brand.key,
        'x-user-token': authToken,
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ old_user_id: oldUserId }),
    })
    if (!res.ok) return false
    const data = (await res.json()) as { success?: boolean }
    return Boolean(data?.success)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[decagon] conversation link failed; continuing without it', err)
    return false
  }
}

// Identify the visitor to Decagon. Anonymous visitors get our own persisted
// decagon_anonymous_<uuid> id (so we can link it later — the id survives a login
// page-reload). When logged in with a pending anon id, we first rebind that
// conversation to the authenticated user server-side — must happen BEFORE
// switching identity (per Decagon) — then setUserId + setUserIdAuth. Metadata
// is sent for every visitor.
async function identifyDecagonUser(): Promise<void> {
  const duet = window.duet
  if (!duet) return

  const auth = await fetchDecagonAuth()
  cachedAuth = auth

  if (auth) {
    const anonId = readAnonId()
    if (anonId && (await linkDecagonConversation(anonId))) {
      window.localStorage.removeItem(DECAGON_ANON_ID_KEY)
    }
    // Apply the signature BEFORE switching the user id (per Decagon support):
    // setUserId triggers a conversation-history fetch, so the auth must already
    // be set or that fetch 401s and the (linked) conversation never gets
    // signature-verified. Order matters — setUserIdAuth first, then setUserId.
    duet.setUserIdAuth?.(auth.signature, auth.epoch)
    duet.setUserId?.(auth.user_id)
  } else {
    // Anonymous — use our own persisted id so the conversation can be linked on login.
    duet.setUserId?.(getOrCreateAnonId())
  }

  // setMetadata REPLACES the whole object, so send the full set in one call —
  // brand + current page for everyone, plus identity fields when signed in.
  duet.setMetadata?.(buildDecagonMetadata(auth))
}

// Re-send metadata with the current URL after a client-side route change (the
// widget persists across SPA navigation). Reuses the cached auth so identity
// fields are preserved (setMetadata replaces the whole object). No-op until the
// widget is ready — the initial DECAGON_LOADED identify covers first load.
export function refreshDecagonMetadata(): void {
  if (typeof window === 'undefined') return
  const duet = window.duet
  if (!duet?.setMetadata) return
  duet.setMetadata(buildDecagonMetadata(cachedAuth))
}

// Full metadata object Decagon receives via setMetadata. Keys (brand_id,
// page_url, ...) are what Decagon AOPs/guidelines reference. Exported for tests.
export function buildDecagonMetadata(auth: DecagonAuth | null): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    brand_id: brand.key,
    page_url: typeof window === 'undefined' ? '' : window.location.href,
  }
  if (auth) {
    // Forward every non-auth field mono sent (email/name/account_type/is_vip/…),
    // skipping nulls. mono is the single source of which keys exist.
    for (const [key, value] of Object.entries(auth)) {
      if (DECAGON_AUTH_KEYS.includes(key) || value === null || value === undefined) continue
      metadata[key] = value
    }
  }
  // setMetadata REPLACES the whole metadata object — two calls means last one
  // wins. The DECAGON_LOADED pin fires first, so every full payload built here
  // (signed-in identify, client-nav refresh) must carry the working-branch pin
  // too, or that replace silently erases it before the conversation is created.
  const branch = getBranch()
  if (branch) metadata._decagon_branch_name = branch
  return metadata
}

function whenDuetReady(cb: () => void): void {
  let attempts = 0
  ;(function check() {
    if (window.duet && typeof window.duet.setEventListener === 'function') {
      cb()
    } else if (attempts++ < 100) {
      window.setTimeout(check, 100)
    }
  })()
}

// GSE-11950: fire ONE start_decagon_chat Rudderstack event per widget open —
// parity with the Rails widget partial (app/views/decagon/_widget.html.slim).
// The Decagon SDK has no "widget opened" event; a setEventListener trace shows
// DUET_USER_FOCUS_INPUT fires exactly once per open (verified on Kettle
// order-tracking) and DUET_DEINITIALIZE fires on close, so we fire on the first
// focus after an open and re-arm on close — no double-fire on re-render.
let chatOpenTracked = false

// order_id per surface: in Kettle it is the URL query param (/order-tracking?
// order_id=..., /order-confirmation?order_id=...); fall back to an order-tracking
// link href (parity with the Rails resolver). null when not in an order context.
function resolveOrderId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('order_id')
    if (fromQuery) return fromQuery
  } catch {
    /* ignore malformed query */
  }
  const link = document.querySelector('a[href*="order_id="]')
  if (link) {
    const match = (link.getAttribute('href') || '').match(/[?&]order_id=([^&#]+)/)
    if (match) return decodeURIComponent(match[1])
  }
  return null
}

function decagonDeviceType(): 'mobile' | 'desktop' {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent || ''
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'mobile' : 'desktop'
}

// Property schema mirrors the Rails event. `brand` is injected by
// trackRudderstackEvent; user_id/logged_in come from the cached signature-auth
// (anonymous visitors carry only anonymous_id via Rudderstack identify).
function fireStartDecagonChat(): void {
  trackRudderstackEvent('start_decagon_chat', {
    logged_in: Boolean(cachedAuth),
    user_id: cachedAuth?.user_id ?? null,
    order_id: resolveOrderId(),
    cart_id: null, // cart context is React-only on Kettle; not resolvable here
    source_page: typeof window === 'undefined' ? '' : window.location.pathname,
    device_type: decagonDeviceType(),
    timestamp: new Date().toISOString(),
  })
}

function loadDecagon(slug: string): void {
  if (typeof window === 'undefined') return

  injectDecagonScript(slug)

  whenDuetReady(() => {
    window.duet!.setEventListener!((event) => {
      if (event?.eventName === 'DECAGON_LOADED') {
        // Route this conversation to a working branch. MUST happen here, before
        // the conversation is created — setting _decagon_branch_name after the
        // conversation starts has no effect. No-op when neither the localStorage
        // key nor NEXT_PUBLIC_DECAGON_BRANCH is set (the production default).
        const branch = getBranch()
        if (branch) {
          window.duet!.setMetadata?.({ _decagon_branch_name: branch })
          // eslint-disable-next-line no-console
          console.info('[decagon] conversation pinned to branch:', branch)
        }
        void identifyDecagonUser()
      } else if (event?.eventName === 'DUET_USER_FOCUS_INPUT') {
        // First focus after an open == the widget was opened.
        if (!chatOpenTracked) {
          chatOpenTracked = true
          fireStartDecagonChat()
        }
      } else if (event?.eventName === 'DUET_DEINITIALIZE') {
        // Widget closed — re-arm for the next open.
        chatOpenTracked = false
      }
      // The SDK has a SINGLE setEventListener slot, so every event branch must
      // live in this one callback — never register a second listener. The
      // agent's open_cart tool event is re-broadcast as a DOM CustomEvent so UI
      // (DecagonPaymentDrawer) can react without coupling to the SDK. Same
      // pattern as BrazeBanner's brazeBannerHeightChange event.
      if (event?.eventName === 'CLIENT_EVENT' && event.name === 'open_cart') {
        window.dispatchEvent(new CustomEvent('decagon:open-cart', { detail: event.data }))
      }
      // The agent's @open_login tool, re-broadcast the same way: the sign-in popover
      // (DecagonLoginPopover) verifies a one-time code and posts the customer's identity back as
      // conversation metadata, so the booking lands on their real account instead of a guest.
      if (event?.eventName === 'CLIENT_EVENT' && event.name === 'open_login') {
        window.dispatchEvent(new CustomEvent('decagon:open-login', { detail: event.data }))
      }
      // CROSS-APP LOGIN. @verify_login_code signs the customer in INSIDE the chat
      // and relays their own web session token here (partner-api ff92f99), so one
      // one-time code signs in both the chat and this browser. Unlike open_cart /
      // open_login this is NOT re-broadcast as a DOM event: there is no UI to
      // react, and the session must be established whether or not any particular
      // component happens to be mounted. So it goes straight to the store.
      //
      // WHAT IT FIXES: without it an in-chat sign-in leaves the browser logged
      // out — order-confirmation has no session to hydrate, and a fallback to the
      // web checkout mints a GUEST. Measured on O092681B8A445, which landed on a
      // guest despite the customer having just signed in with a code.
      if (event?.eventName === 'CLIENT_EVENT' && event.name === 'set_auth') {
        // Shape-checked, never trusted raw — see parseSetAuthPayload.
        const data = parseSetAuthPayload(event.data)
        if (data) {
          // Tell useDecagon's auth-change effect this token came from the chat, so
          // it does not re-identify (and replace the metadata the tool just wrote).
          markChatSignIn(data.token)
          // mobileVerified/termsAccepted are MEANINGFULLY FALSE: setAuth honours
          // them only when `typeof === 'boolean'` and otherwise keeps what the
          // store already held, so passing them through as-is (undefined when
          // absent) is exactly right — do NOT coerce with `?? false`, which would
          // assert unverified on an account that never said.
          useSessionStore
            .getState()
            .setAuth(
              data.token,
              data.userId ?? null,
              data.userEmail ?? null,
              data.userRoles ?? [],
              data.mobileVerified,
              data.termsAccepted,
            )
          // ⚠️ DO NOT call refreshDecagonMetadata() here. setMetadata REPLACES the
          // whole metadata object, and cachedAuth is still null on this path — so
          // a refresh would erase the customer_* keys @verify_login_code just
          // wrote, and the branch pin with them. The same replace used to arrive
          // anyway via useDecagon's auth-change effect; markChatSignIn above is
          // what stops it.
          // eslint-disable-next-line no-console
          console.info('[decagon] browser signed in from chat:', data.userId ?? '(no id)')
        }
      }
    })
  })
}

export const scheduleDecagonLoad = (slug: string | undefined = DECAGON_SLUG): void => {
  if (!slug || typeof window === 'undefined' || decagonLoadScheduled) return

  decagonLoadScheduled = true

  const idleLoader = (): void => {
    loadDecagon(slug)
  }

  const ric = (
    window as Window &
      typeof globalThis & {
        requestIdleCallback?: (cb: () => void, options?: IdleRequestOptions) => number
      }
  ).requestIdleCallback

  if (ric) {
    ric(idleLoader, { timeout: 5000 })
  } else {
    window.setTimeout(idleLoader, 1500)
  }
}

export const useDecagon = (options: { enabled?: boolean } = {}): void => {
  const { enabled = true } = options
  const pathname = usePathname()
  const authToken = useSessionStore((s) => s.authToken)
  const queryClient = useQueryClient()
  const firstRun = useRef(true)
  const lastAuthToken = useRef(authToken)

  useEffect(() => {
    if (!enabled) return
    scheduleDecagonLoad()
  }, [enabled])

  // On client-side navigation (not the initial mount — the load-time identify
  // already sent metadata for that URL), refresh page_url in Decagon metadata.
  useEffect(() => {
    if (!enabled) return
    if (firstRun.current) {
      firstRun.current = false
      return
    }
    refreshDecagonMetadata()
  }, [enabled, pathname])

  // Re-identify on an in-session login/logout without a page reload. identifyUser
  // handles the anon-conversation link on login and a fresh anon id on logout.
  // Skips the initial mount (the load-time identify ran). When login is a full
  // page reload (the common case here), the post-login load path covers it
  // instead — our anon id persists across the reload.
  useEffect(() => {
    if (!enabled) return
    if (lastAuthToken.current === authToken) return
    lastAuthToken.current = authToken

    // Signed in FROM the chat (set_auth relay or the sign-in popover): the
    // conversation already carries this identity, so skip the re-identify — its
    // setMetadata would REPLACE and erase what the tool just wrote (see
    // chatSignInToken). What the app itself still needs is what every other
    // login gets from its window.location.reload(): a fresh currentUser.
    // useUserApi keys on ['currentUser'] alone with a 5-minute staleTime, so
    // without this a guest-session browser keeps rendering the GUEST's profile
    // (name, mobileVerified, shipments) after the customer signed in.
    if (consumeChatSignIn(authToken)) {
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      return
    }

    if (typeof window !== 'undefined' && window.duet) void identifyDecagonUser()
  }, [enabled, authToken, queryClient])
}

/**
 * Best-effort programmatic open of the Decagon widget. Decagon also renders its
 * own launcher, so this is only used to route existing "chat" buttons.
 */
export const openDecagon = (): void => {
  if (typeof window === 'undefined') return

  const open = (): void => {
    const duet = window.duet
    if (!duet) return
    if (typeof duet.open === 'function') {
      duet.open()
    } else if (typeof duet === 'function') {
      duet('open')
    }
  }

  if (window.duet) {
    open()
    return
  }

  // Not loaded yet — kick the load and open once ready (parity with Zendesk's
  // lazy-load-on-click), so an early header-button click isn't a dead click.
  scheduleDecagonLoad()
  whenDuetReady(open)
}
